import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import Logo from './Logo';
import { Menu, X, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const TEMPLATE_LINKS = [
  { label: 'Photographers', href: '/invoice-template/photographers' },
  { label: 'Musicians', href: '/invoice-template/musicians' },
  { label: 'Designers', href: '/invoice-template/designers' },
  { label: 'Filmmakers', href: '/invoice-template/filmmakers' },
  { label: 'Freelancers', href: '/invoice-template/freelancers' },
  { label: 'Free Invoice Generator', href: '/free-invoice-generator' },
];

/**
 * Public marketing header shared across Landing and the marketing sibling pages
 * (About, Support, Terms, Privacy, FreeInvoiceGenerator, BlogPublicHeader).
 *
 * secondaryLabel/secondaryHref: the first nav link (e.g. "About" → "/about",
 * or on the About page itself "Home" → "/"). Kept as props so the label
 * naturally adapts to the page you're on.
 *
 * On mobile (< sm), About + Log in collapse into a hamburger; Sign up stays
 * inline as the primary CTA so the value prop is always one tap away.
 */
const PublicHeader = ({
  secondaryLabel = 'About',
  secondaryHref = '/about',
  logoHref = '/',
  fixed = true,
}) => {
  const [open, setOpen] = useState(false);
  const headerBase = fixed ? 'fixed top-0 left-0 right-0 z-50' : 'w-full';

  return (
    <header
      className={`${headerBase} bg-background/85 backdrop-blur border-b border-foreground/10`}
      data-testid="public-header"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to={logoHref} className="flex items-center gap-2" data-testid="logo-link">
          <Logo />
        </Link>

        {/* Desktop nav */}
        <div className="hidden sm:flex items-center gap-3 sm:gap-4">
          <a href="/#pricing">
            <Button variant="ghost" data-testid="nav-pricing-btn">Pricing</Button>
          </a>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-1" data-testid="nav-templates-btn">
                Templates <ChevronDown className="w-3.5 h-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" data-testid="nav-templates-menu">
              {TEMPLATE_LINKS.map((item) => (
                <DropdownMenuItem key={item.href} asChild>
                  <Link to={item.href} data-testid={`nav-templates-item-${item.href.split('/').pop()}`}>
                    {item.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Link to={secondaryHref}>
            <Button variant="ghost" data-testid="nav-secondary-btn">{secondaryLabel}</Button>
          </Link>
          <Link to="/login">
            <Button variant="ghost" data-testid="login-btn">Log in</Button>
          </Link>
          <Link to="/register">
            <Button data-testid="signup-btn" className="rounded-full">Sign up</Button>
          </Link>
        </div>

        {/* Mobile: Sign up + hamburger */}
        <div className="flex sm:hidden items-center gap-2">
          <Link to="/register">
            <Button data-testid="signup-btn-mobile" size="sm" className="rounded-full">Sign up</Button>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          className="sm:hidden border-t border-foreground/10 bg-background"
          data-testid="mobile-menu-drawer"
        >
          <nav className="max-w-7xl mx-auto px-6 py-3 flex flex-col gap-1">
            <a href="/#pricing" onClick={() => setOpen(false)}>
              <Button variant="ghost" className="w-full justify-start" data-testid="mobile-nav-pricing">
                Pricing
              </Button>
            </a>
            <div className="px-4 pt-2 pb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/50">
              Templates
            </div>
            {TEMPLATE_LINKS.map((item) => (
              <Link key={item.href} to={item.href} onClick={() => setOpen(false)}>
                <Button
                  variant="ghost"
                  className="w-full justify-start pl-6 font-normal"
                  data-testid={`mobile-nav-templates-item-${item.href.split('/').pop()}`}
                >
                  {item.label}
                </Button>
              </Link>
            ))}
            <Link to={secondaryHref} onClick={() => setOpen(false)}>
              <Button variant="ghost" className="w-full justify-start" data-testid="mobile-nav-secondary">
                {secondaryLabel}
              </Button>
            </Link>
            <Link to="/login" onClick={() => setOpen(false)}>
              <Button variant="ghost" className="w-full justify-start" data-testid="mobile-nav-login">
                Log in
              </Button>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default PublicHeader;
